/**
 * RoundRockRebatePage — `/round-rock-rebate`
 *
 * Single-purpose conversion landing built specifically for Nextdoor + paid
 * social clicks targeting Round Rock zip codes. The destination URL for the
 * Nextdoor ad points here.
 *
 * Design constraints:
 * - Two-path split above the fold (City of RR water customers vs. everyone
 *   else) because the rebate only applies to ~60% of "Round Rock" residents
 *   — the other 40% (Crossroads, BCMUD) get nothing from the city.
 * - Minimal nav distraction — no global header CTAs competing for the click.
 * - Authentic Derwin voice. No emoji in body (Nextdoor's policy bot is twitchy
 *   and we want the page to mirror the ad copy that passed review).
 * - UTM params land in sessionStorage automatically (see AuthContext.js) so
 *   any signup from this page is attributed to whichever campaign drove it.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { FaqJsonLd } from '../components/FaqJsonLd';
import { Button } from '../components/ui/button';
import {
  Droplets, MapPin, Clock, TrendingDown, ArrowRight,
  CheckCircle2, FileCheck, Cpu, Sprout,
} from 'lucide-react';

const PAGE_URL = 'https://getturfpulse.com/round-rock-rebate';

const FAQS = [
  {
    q: "How do I know if I'm a City of Round Rock water customer?",
    a: "Look at your water bill. If it says 'City of Round Rock Utilities' as the issuer, you qualify for the rebate program. If it says 'Crossroads Utility Services', 'Brushy Creek MUD', 'Travis County WCID', or any other private/MUD provider, you don't — the rebate is funded by city ratepayers and only available to them. The smart-irrigation tool still works either way; the city rebate is just a bonus the city customers can claim.",
  },
  {
    q: "Is the $500/yr rebate cap real?",
    a: "Yes — straight from the City of Round Rock's 'Smart Irrigation Rebate' guidelines (last revised October 2025). The structure is 50% of cost capped per item: $100 for a WaterSense controller, $50 for rain/soil/freeze sensors, $50/zone for drip conversions, $25/zone for zone capping, $200 for a 50% irrigation checkup, and a hard $500/year ceiling per residential account. You can mix and match across categories. Verify on the official city rebate page before purchasing — these things can change.",
  },
  {
    q: "What about Round Rock's 10am-7pm watering ordinance?",
    a: "City-of-Round-Rock code prohibits irrigation between 10am and 7pm year-round. It applies regardless of which water provider you use — it's a city ordinance, not a water-bill thing. The TurfPulse smart-irrigation tool respects this window automatically; you set your schedule and it won't fire during ordinance hours even if soil moisture drops below your threshold. It'll queue the run for after 7pm instead. No more $250 ordinance fines.",
  },
  {
    q: "I'm on Crossroads — what's actually in this for me?",
    a: "Same outcome (smart irrigation that pays attention to your dirt, not just the forecast), without the rebate. My water bill on Crossroads dropped about $42/month once I stopped guessing. No rebate is coming our way — Crossroads is a private utility and doesn't run a conservation rebate program. So the only return is the bill savings + the ordinance-fine avoidance. For a typical Round Rock yard, that's still $30-50/mo back in your pocket from May through October.",
  },
  {
    q: "Do I need to buy probes and a controller to use this?",
    a: "If you already have a Rachio (Gen 2 or 3), you're 80% of the way there — TurfPulse uses Rachio's API to fire your zones. Soil probes are optional but recommended; the cheapest path is one Ecowitt GW2000 gateway plus 2-3 WH51 probes (about $120 total on Amazon). If you're starting from a basic clock-based controller, a WaterSense smart controller is the eligible rebate purchase the city actually subsidizes — Rachio 3 and Hydrawise both qualify.",
  },
  {
    q: "How long does the rebate application take?",
    a: "If you have receipts, photos of the installed equipment, and your water bill in front of you — about 20 minutes. The form is mostly straightforward but Round Rock requires you to include 'before' photos of your existing controller AND 'after' photos of the new install, plus the WaterSense product label. The free tool walks you through each requirement in order so you don't get to the bottom of the form and realize you're missing a doc.",
  },
];

const BREADCRUMBS = [
  { label: 'Home', path: '/' },
  { label: 'Round Rock Smart Irrigation Rebate Guide', path: '/round-rock-rebate' },
];

export default function RoundRockRebatePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-stone-50 dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950">
      <SEO
        title="Round Rock Smart Irrigation Rebate Guide — Get Up to $500/yr Back"
        description="A neighbor's honest walkthrough of the City of Round Rock smart-irrigation rebate (up to $500/yr) — plus what to do if you're on Crossroads or Brushy Creek MUD and don't qualify."
        canonical={PAGE_URL}
        type="article"
      />
      <BreadcrumbJsonLd items={BREADCRUMBS} />
      <FaqJsonLd items={FAQS} />

      {/* Minimal top nav — just an unobtrusive "back to TurfPulse" so the
          page doesn't feel orphaned but doesn't compete with the CTA. */}
      <header className="border-b border-emerald-100 dark:border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link
            to="/"
            className="text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-300 transition-colors"
            data-testid="rebate-back-home"
          >
            ← TurfPulse
          </Link>
          <div className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400">
            <MapPin className="w-3.5 h-3.5" />
            <span>Round Rock, TX</span>
          </div>
        </div>
      </header>

      {/* ───────── HERO ───────── */}
      <section className="max-w-5xl mx-auto px-4 pt-10 pb-6 sm:pt-16 sm:pb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-200 text-xs font-medium mb-5">
          <Droplets className="w-3.5 h-3.5" />
          Round Rock-specific guide · updated 2026
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 dark:text-stone-100 mb-4 max-w-3xl">
          The Round Rock smart-irrigation rebate most homeowners don't know about
        </h1>

        <p className="text-base sm:text-lg text-stone-600 dark:text-stone-400 max-w-2xl leading-relaxed mb-3">
          City of Round Rock water customers can claim <strong className="text-stone-900 dark:text-stone-100">up to $500/year</strong> back on
          smart sprinkler upgrades. About 60% of "Round Rock" residents qualify. The other 40% — on Crossroads or
          Brushy Creek MUD — don't. This page is honest about which is which, and what to do either way.
        </p>
        <p className="text-sm text-stone-500 dark:text-stone-500 max-w-2xl italic">
          Built by Derwin — neighbor on the east side. I'm on Crossroads, so I don't get the rebate either.
          That's why I built the savings tool.
        </p>
      </section>

      {/* ───────── TWO-PATH SPLIT ───────── */}
      <section className="max-w-5xl mx-auto px-4 pb-12">
        <div className="grid sm:grid-cols-2 gap-4">
          {/* Path A — City of RR water customers */}
          <div className="rounded-2xl border-2 border-emerald-300 dark:border-emerald-700 bg-white dark:bg-zinc-900 p-6 sm:p-7 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-3">
              <FileCheck className="w-4 h-4" />
              IF YOU'RE ON CITY OF ROUND ROCK WATER
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-stone-900 dark:text-stone-100 mb-3">
              You can claim up to $500/yr in rebates.
            </h2>
            <ul className="space-y-2 text-sm text-stone-600 dark:text-stone-300 mb-5">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />50% off a WaterSense controller (up to $100)</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />50% off rain/soil/freeze sensors (up to $50)</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />$50/zone for drip conversions</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />50% off a professional irrigation checkup (up to $200)</li>
            </ul>
            <Button asChild className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="path-city-cta">
              <Link to="/rachio/rebate-helper">
                Walk me through the rebate application
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-3">
              Free — no email required to look. Verifies your address against the city customer roster and gives you the right forms in the right order.
            </p>
          </div>

          {/* Path B — Crossroads, BCMUD, other */}
          <div className="rounded-2xl border-2 border-amber-300 dark:border-amber-800 bg-white dark:bg-zinc-900 p-6 sm:p-7 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 dark:text-amber-400 mb-3">
              <TrendingDown className="w-4 h-4" />
              IF YOU'RE ON CROSSROADS, BCMUD, OR ANOTHER PROVIDER
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-stone-900 dark:text-stone-100 mb-3">
              No rebate. But you can still cut $30-50/month.
            </h2>
            <ul className="space-y-2 text-sm text-stone-600 dark:text-stone-300 mb-5">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />Stop watering when it's about to rain (forecast-aware)</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />Stop watering when the soil is already wet (probe-aware)</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />Auto-respect the 10am-7pm city ordinance (no fines)</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />My own bill dropped ~$42/mo. Yours probably will too.</li>
            </ul>
            <Button asChild className="w-full bg-amber-700 hover:bg-amber-800 text-white" data-testid="path-savings-cta">
              <Link to="/register">
                Set up the smart-irrigation tool — free
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-3">
              Works with your existing Rachio controller. Optional Ecowitt soil probes (~$120 total) make it dramatically smarter.
            </p>
          </div>
        </div>
      </section>

      {/* ───────── HOW IT WORKS ───────── */}
      <section className="bg-stone-50 dark:bg-zinc-900/50 border-y border-stone-200 dark:border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-100 mb-2">
            How the tool actually works
          </h2>
          <p className="text-stone-600 dark:text-stone-400 mb-8 max-w-2xl">
            Three pieces. The tool is the glue.
          </p>

          <div className="grid sm:grid-cols-3 gap-6">
            <div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
                <Droplets className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
              </div>
              <h3 className="font-semibold text-stone-900 dark:text-stone-100 mb-1">1. Soil probes report moisture</h3>
              <p className="text-sm text-stone-600 dark:text-stone-400">
                Ecowitt WH51 probes (one per zone) measure actual volumetric water content in your dirt — not a forecast,
                not a model, the real thing.
              </p>
            </div>
            <div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
                <Cpu className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
              </div>
              <h3 className="font-semibold text-stone-900 dark:text-stone-100 mb-1">2. TurfPulse does the math</h3>
              <p className="text-sm text-stone-600 dark:text-stone-400">
                We combine soil moisture, NOAA forecast, evapotranspiration, and the 10am-7pm ordinance window
                to decide if a zone needs water — and how much.
              </p>
            </div>
            <div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
                <Sprout className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
              </div>
              <h3 className="font-semibold text-stone-900 dark:text-stone-100 mb-1">3. Rachio fires the zones</h3>
              <p className="text-sm text-stone-600 dark:text-stone-400">
                We hit Rachio's API to start the right zones at the right time. Skips storms automatically.
                Never watering between 10am and 7pm. You stop guessing.
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/methodology/probe-driven-irrigation"
              className="text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
              data-testid="methodology-link"
            >
              Read the full methodology (with citations)
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <span className="text-stone-300 dark:text-stone-700">·</span>
            <Link
              to="/blog/ecowitt-to-rachio-i-tried-all-4-options-and-shipped-one"
              className="text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
              data-testid="blog-link"
            >
              Why I built this instead of using Home Assistant
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ───────── ORDINANCE CALLOUT ───────── */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="rounded-2xl bg-emerald-700 dark:bg-emerald-800 text-white p-6 sm:p-10">
          <div className="flex items-start gap-4">
            <Clock className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl sm:text-2xl font-bold mb-2">
                The 10am-7pm watering ordinance applies to all Round Rock residents
              </h2>
              <p className="text-emerald-50 mb-3 leading-relaxed">
                Doesn't matter who bills you for water — City of Round Rock, Crossroads, BCMUD, doesn't matter.
                The ordinance is a city code that applies inside the city limits. Year-round. Fines start at $250 for first
                offense and escalate. The TurfPulse tool simply will not fire your zones inside the prohibited window,
                even when soil readings would otherwise call for it. It re-queues the run for after 7pm.
              </p>
              <p className="text-emerald-100 text-sm">
                If your current irrigation runs on a clock-only schedule, you've probably already broken this ordinance
                this season without realizing it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className="bg-stone-50 dark:bg-zinc-900/50 border-t border-stone-200 dark:border-zinc-800">
        <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-100 mb-8">
            Questions Round Rock neighbors actually ask
          </h2>
          <div className="space-y-5">
            {FAQS.map((f, i) => (
              <details
                key={i}
                className="group rounded-xl bg-white dark:bg-zinc-900 border border-stone-200 dark:border-zinc-800 p-5 open:shadow-sm transition-shadow"
                data-testid={`faq-${i}`}
              >
                <summary className="font-semibold text-stone-900 dark:text-stone-100 cursor-pointer list-none flex items-center justify-between gap-4">
                  <span>{f.q}</span>
                  <span className="text-stone-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-stone-600 dark:text-stone-300 leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── FINAL CTA ───────── */}
      <section className="max-w-3xl mx-auto px-4 py-12 sm:py-16 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-100 mb-3">
          Set up in about 20 minutes
        </h2>
        <p className="text-stone-600 dark:text-stone-400 mb-7 max-w-xl mx-auto">
          Free to set up. Free to use the rebate walkthrough. The probe-driven side has a founder tier at $5/month if you want it,
          but the basics work without paying anything.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="final-cta-start">
            <Link to="/register">
              Start free — see how it works
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" data-testid="final-cta-rebate">
            <Link to="/rachio/rebate-helper">
              Just the rebate help
            </Link>
          </Button>
        </div>
        <p className="text-xs text-stone-500 dark:text-stone-500 mt-6">
          No spam, no shared data, no marketing emails unless you opt in. Built by a Round Rock homeowner who got tired of guessing.
        </p>
      </section>
    </div>
  );
}
